import { IntegrationServices } from '@sequencehq/api/utils/commonEnums'
import { SimpleModal, Box } from '@sequencehq/core-components'
import { FC } from 'react'
import { GreyGrey80, Primary13Regular } from '@sequencehq/core-components'
import { getIntegrationName } from 'Integrations/utils/getIntegrationName'
import { useMutation } from '@sequencehq/api/utils'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from '@sequencehq/core-components'

const useUnlinkBillingScheduleModal = (props: {
  service: IntegrationServices
  billingScheduleId: string
}) => {
  const { displayNotification } = useNotifications()

  const unlinkingMutation = useMutation(
    dashboard20240730Client.deleteLinkedEntity
  )

  const serviceName = getIntegrationName(props.service)
  const onUnlink = async (): Promise<boolean> => {
    try {
      await unlinkingMutation.mutateAsync({
        type: 'BILLING_SCHEDULE',
        id: props.billingScheduleId,
        service: props.service
      })
      displayNotification(`Billing schedule unlinked from ${serviceName}`, {
        type: 'success'
      })
      return true
    } catch {
      displayNotification(
        `Could not unlink billing schedule from ${serviceName}`,
        {
          type: 'error'
        }
      )
      return false
    }
  }

  return { serviceName, onUnlink, unlinking: unlinkingMutation.isPending }
}

export const UnlinkBillingScheduleModal: FC<{
  billingScheduleId: string
  service: IntegrationServices
  onClose: () => void
}> = props => {
  const hook = useUnlinkBillingScheduleModal({
    service: props.service,
    billingScheduleId: props.billingScheduleId
  })

  return (
    <SimpleModal
      isOpen
      variant="dialog"
      onClose={props.onClose}
      data-testid="modal.unlinkBillingSchedule"
      title={`Unlink billing schedule from ${hook.serviceName}`}
      isDestructive
      disableSubmitOnEnter
      submitButtonText="Unlink"
      submittingButtonText="Unlinking..."
      cancelButtonText="Cancel"
      submitting={hook.unlinking}
      onSubmit={hook.onUnlink}
    >
      <Box color={GreyGrey80} {...Primary13Regular} flex={1}>
        This will unlink the customer from {hook.serviceName} immediately. You
        can re-link the customer again if required.
      </Box>
    </SimpleModal>
  )
}
